import React, { useState } from 'react';
import { SaveIcon, XIcon, LinkIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
interface ExternalSubscriptionFormProps {
  onClose: () => void;
  onRunSync: () => void;
  locale: string;
}
export function ExternalSubscriptionForm({
  onClose,
  onRunSync,
  locale
}: ExternalSubscriptionFormProps) {
  const [formData, setFormData] = useState({
    enabled: true,
    provider: 'openexchangerates',
    apiKey: 'YOUR_API_KEY_HERE',
    syncTime: '03:00',
    defaultRateType: 'External',
    baseCurrencies: ['USD', 'EUR', 'GBP'],
    lastSyncDate: '',
    syncStatus: ''
  });
  const [testStatus, setTestStatus] = useState<'none' | 'success' | 'error'>('none');
  const [testMessage, setTestMessage] = useState('');
  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  // Handle currency selection
  const handleCurrencyToggle = (currency: string) => {
    setFormData(prev => {
      const newCurrencies = prev.baseCurrencies.includes(currency) ? prev.baseCurrencies.filter(c => c !== currency) : [...prev.baseCurrencies, currency];
      return {
        ...prev,
        baseCurrencies: newCurrencies
      };
    });
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would save the configuration to the backend
    alert('External provider settings saved successfully.');
    onClose();
  };
  // Handle test connection
  const handleTestConnection = () => {
    // In a real app, this would test the API connection
    setTestStatus('success');
    setTestMessage('Connection successful! API key is valid.');
    // Simulate an API call
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        lastSyncDate: new Date().toISOString(),
        syncStatus: 'Success'
      }));
    }, 1000);
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-7992">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-7993">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7994">
          External Exchange Rate Provider
        </h3>
        <button onClick={onClose} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7995">
          <XIcon className="h-5 w-5" data-id="element-7996" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-7997">
        {/* Enable/Disable */}
        <div className="flex items-center justify-between" data-id="element-7998">
          <div className="flex items-center" data-id="element-7999">
            <input id="enabled" name="enabled" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={formData.enabled} onChange={handleChange} data-id="element-8000" />
            <label htmlFor="enabled" className="ml-2 block text-sm text-gray-700" data-id="element-8001">
              Enable Automatic Import
            </label>
          </div>
          {formData.lastSyncDate && <div className="text-xs text-gray-500" data-id="element-8002">
              Last synced:{' '}
              {new Date(formData.lastSyncDate).toLocaleString(locale)}
              {formData.syncStatus === 'Success' && <span className="ml-1 text-green-600" data-id="element-8003">✓</span>}
            </div>}
        </div>
        {/* Provider Selection */}
        <div data-id="element-8004">
          <label htmlFor="provider" className="block text-sm font-medium text-gray-700" data-id="element-8005">
            Exchange Rate Provider
          </label>
          <select id="provider" name="provider" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={formData.provider} onChange={handleChange} disabled={!formData.enabled} data-id="element-8006">
            <option value="openexchangerates" data-id="element-8007">Open Exchange Rates</option>
            <option value="fixer" data-id="element-8008">Fixer.io</option>
            <option value="xe" data-id="element-8009">XE Currency Data</option>
            <option value="currencylayer" data-id="element-8010">Currency Layer</option>
          </select>
        </div>
        {/* API Key */}
        <div data-id="element-8011">
          <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700" data-id="element-8012">
            API Key
          </label>
          <div className="mt-1 flex rounded-md shadow-sm" data-id="element-8013">
            <input type="text" name="apiKey" id="apiKey" className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-md sm:text-sm border-gray-300" placeholder="Enter your API key" value={formData.apiKey} onChange={handleChange} disabled={!formData.enabled} data-id="element-8014" />
          </div>
          <p className="mt-2 text-xs text-gray-500" data-id="element-8015">
            You can obtain an API key by signing up at{' '}
            <a href="#" onClick={e => {
            e.preventDefault();
            alert('This would link to the provider website in a real app.');
          }} className="text-indigo-600 hover:text-indigo-500" data-id="element-8016">
              {formData.provider === 'openexchangerates' ? 'openexchangerates.org' : formData.provider === 'fixer' ? 'fixer.io' : formData.provider === 'xe' ? 'xe.com/xecurrencydata/' : 'currencylayer.com'}
            </a>
          </p>
        </div>
        {/* Test Connection */}
        {formData.enabled && <div data-id="element-8017">
            <button type="button" onClick={handleTestConnection} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8018">
              <LinkIcon className="h-4 w-4 mr-2" data-id="element-8019" />
              Test Connection
            </button>
            {testStatus === 'success' && <div className="mt-2 flex items-center text-sm text-green-600" data-id="element-8020">
                <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-8021" />
                {testMessage}
              </div>}
            {testStatus === 'error' && <div className="mt-2 flex items-center text-sm text-red-600" data-id="element-8022">
                <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-8023" />
                {testMessage}
              </div>}
          </div>}
        {/* Sync Settings */}
        <div data-id="element-8024">
          <label htmlFor="syncTime" className="block text-sm font-medium text-gray-700" data-id="element-8025">
            Daily Sync Time (UTC)
          </label>
          <input type="time" name="syncTime" id="syncTime" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={formData.syncTime} onChange={handleChange} disabled={!formData.enabled} data-id="element-8026" />
        </div>
        {/* Default Rate Type */}
        <div data-id="element-8027">
          <label htmlFor="defaultRateType" className="block text-sm font-medium text-gray-700" data-id="element-8028">
            Default Rate Type for Imported Rates
          </label>
          <select id="defaultRateType" name="defaultRateType" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={formData.defaultRateType} onChange={handleChange} disabled={!formData.enabled} data-id="element-8029">
            <option value="Bank" data-id="element-8030">Bank</option>
            <option value="Spot" data-id="element-8031">Spot</option>
          </select>
        </div>
        {/* Base Currencies */}
        <div data-id="element-8032">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8033">
            Supported Base Currencies
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-8034">
            {['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CNY', 'INR'].map(currency => <div key={currency} className="flex items-center" data-id="element-8035">
                  <input id={`currency-${currency}`} name={`currency-${currency}`} type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={formData.baseCurrencies.includes(currency)} onChange={() => handleCurrencyToggle(currency)} disabled={!formData.enabled} data-id="element-8036" />
                  <label htmlFor={`currency-${currency}`} className="ml-2 block text-sm text-gray-700" data-id="element-8037">
                    {currency}
                  </label>
                </div>)}
          </div>
          <p className="mt-2 text-xs text-gray-500" data-id="element-8038">
            Select currencies that should be used as base currencies for rate
            conversion.
          </p>
        </div>
        {/* Actions */}
        <div className="pt-5 border-t border-gray-200" data-id="element-8039">
          <div className="flex justify-between" data-id="element-8040">
            <button type="button" onClick={onRunSync} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" disabled={!formData.enabled} data-id="element-8041">
              Run Sync Now
            </button>
            <div className="flex space-x-3" data-id="element-8042">
              <button type="button" onClick={onClose} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8043">
                Cancel
              </button>
              <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8044">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-8045" />
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>;
}